/**
 * Scope Cell (ORCH-333, ORCH-21 §3.7)
 *
 * Purpose: One permission × dimension cell of a role: nothing, Inherit (the
 *          holder's own attributes), Override (values picked here) or ALL.
 *          Override picks values with the same list renderer a media plan
 *          uses, so a role and a plan always name the same master data; on a
 *          hierarchy any level can be picked, and picking a parent covers
 *          everything under it, including values added later. A dimension
 *          narrowed by another (Media Subtype by Media Type, ORCH-389) offers
 *          only the values related to what the same permission's cell on the
 *          other dimension picked; with no picked values there, everything.
 *          Deliberately outside any form root and any <PickerScope> (ORCH-366):
 *          the `role.*` permissions are not dimensioned, so a role editor may
 *          name any value the tenant can see and a role scope would be a no-op.
 *          Revisit if `role.*` ever becomes dimensioned.
 */

import React from 'react';
import { Box, MenuItem, Select, Typography } from '@mui/material';

import { ListFieldRenderer } from '../../../components/metadata-form/FieldRenderers';
import { ViewField } from '../../../components/metadata-form/types';
import { PickerValues } from '../../../context/PickerScopeContext';
import { Narrowing, useNarrowedPickerValues } from '../../../hooks/useNarrowedPickerValues';
import { FieldDefinition } from '../../../services/metadataService';
import { asList } from '../../../utils/listValues';
import { Scope, ScopeMode } from '../../../types/authz';

const NONE = 'none';

interface ScopeCellProps {
  permissionKey: string;
  permissionLabel: string;
  dimensionKey: string;
  dimensionLabel: string;
  scope: Scope | undefined;
  /** The field the Override picker opens (the bottom of the dimension's chain). */
  fieldDefinition: FieldDefinition | undefined;
  /** The picker's field couldn't be loaded. */
  loadFailed?: boolean;
  readOnly?: boolean;
  /** What the Override picker is narrowed to, when this dimension follows another. */
  narrowing?: Narrowing | null;
  /** The tenant whose values the narrowing reads; needed with `narrowing`. */
  tenantId?: string;
  onChange: (scope: Scope | null) => void;
}

/** Limits the picker inside to the values related to the other dimension's choice. */
const NarrowedValues: React.FC<{
  narrowing: Narrowing;
  tenantId: string;
  children: React.ReactNode;
}> = ({ narrowing, tenantId, children }) => {
  const limits = useNarrowedPickerValues([narrowing], tenantId);
  return <PickerValues values={limits}>{children}</PickerValues>;
};

/** The picker's field: always multiselect, so every level is selectable, and never "Add new". */
export const pickerField = (fd: FieldDefinition): ViewField => ({
  field_id: `role-scope-${fd.fielddef_id}`,
  fielddef_id: fd.fielddef_id,
  tenant_id: fd.tenant_id,
  model_id: 'role',
  storage_type: 'metadata',
  display_order: 0,
  label_override: fd.label,
  field_definition: {
    fielddef_id: fd.fielddef_id,
    label: fd.label,
    datatype: 'list',
    is_multiselect: true,
    creation_access: 'None',
    hierarchy_config: fd.hierarchy_config ?? null,
  },
});

const ScopeCell: React.FC<ScopeCellProps> = ({
  permissionKey,
  permissionLabel,
  dimensionKey,
  dimensionLabel,
  scope,
  fieldDefinition,
  loadFailed,
  readOnly,
  narrowing,
  tenantId,
  onChange,
}) => {
  const mode = scope?.mode ?? NONE;
  const testId = `cell-${permissionKey}-${dimensionKey}`;
  const picker = fieldDefinition && (
    <ListFieldRenderer
      field={pickerField(fieldDefinition)}
      value={scope?.value_ids ?? []}
      readOnly={readOnly}
      disabled={readOnly}
      // ORCH-418: the cell's own ALL mode says "all"; its picker offers no All row
      selectAll={false}
      onChange={(value: unknown) => onChange({ mode: 'override', value_ids: asList(value) })}
    />
  );

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1, minWidth: 140 }}>
      <Select
        size="small"
        value={mode}
        disabled={readOnly}
        onChange={(e) => {
          const next = e.target.value as ScopeMode | typeof NONE;
          onChange(next === NONE ? null : { mode: next, value_ids: [] });
        }}
        inputProps={{
          'aria-label': `${dimensionLabel} scope for ${permissionLabel}`,
          'data-testid': testId,
        }}
        sx={mode === NONE ? { color: 'error.main' } : undefined}
      >
        <MenuItem value={NONE}>– (nothing)</MenuItem>
        <MenuItem value="inherit">Inherit</MenuItem>
        <MenuItem value="override">Pick values</MenuItem>
        <MenuItem value="all">ALL</MenuItem>
      </Select>
      {mode === 'override' &&
        (fieldDefinition ? (
          <Box data-testid={`${testId}-values`}>
            {narrowing && tenantId ? (
              <NarrowedValues narrowing={narrowing} tenantId={tenantId}>
                {picker}
              </NarrowedValues>
            ) : (
              picker
            )}
          </Box>
        ) : loadFailed ? (
          <Typography variant="caption" color="error.main">
            {dimensionLabel} values couldn't be loaded; reload to pick values
          </Typography>
        ) : (
          <Typography variant="caption" color="text.secondary">
            Loading {dimensionLabel} values…
          </Typography>
        ))}
    </Box>
  );
};

export default ScopeCell;
